if (!customElements.get('upsell-carousel')) {
  class UpsellCarousel extends HTMLElement {
    constructor() {
      super();
      this.renderedHandles = new Set();
      this.staticHandles = new Set();
      this.cartProductHandles = new Set();
    }

    connectedCallback() {
      this.style.display = 'block';

      this.querySelectorAll('swiper-slide[data-product-handle]').forEach(slide => {
        this.renderedHandles.add(slide.dataset.productHandle);
        this.staticHandles.add(slide.dataset.productHandle);
      });

      this.cartProductHandles = new Set(
        [...document.querySelectorAll('[data-cart-product-handle]')].map(el => el.dataset.cartProductHandle)
      );

      this.addEventListener('click', e => {
        const btn = e.target.closest('[data-upsell-atc]');
        if (btn) this.handleATC(btn);
      });

      const dynamicLimit = parseInt(this.dataset.dynamicLimit, 10) || 0;
      if (dynamicLimit > 0) {
        this.fetchAndAppendDynamic(dynamicLimit);

        this.onProductAdd = () => setTimeout(() => {
          this.clearDynamicSlides();
          this.fetchAndAppendDynamic(dynamicLimit);
        }, 0);

        this.onCartUpdate = e => {
          if (e.detail?.item_count !== 0) return;
          setTimeout(() => {
            this.clearDynamicSlides();
            this.fetchAndAppendDynamic(dynamicLimit);
          }, 0);
        };

        window.addEventListener('productForm:add', this.onProductAdd);
        window.addEventListener('cart:update', this.onCartUpdate);
      }

      this.updateSlideSignal();
      this.initNavigation();
    }

    disconnectedCallback() {
      if (this.onProductAdd) window.removeEventListener('productForm:add', this.onProductAdd);
      if (this.onCartUpdate) window.removeEventListener('cart:update', this.onCartUpdate);
    }

    clearDynamicSlides() {
      this.renderedHandles = new Set(this.staticHandles);
      this.cartProductHandles = new Set(
        [...document.querySelectorAll('[data-cart-product-handle]')].map(el => el.dataset.cartProductHandle)
      );
      const swiper = this.querySelector('swiper-container');
      swiper?.querySelectorAll('swiper-slide').forEach(s => {
        if (!this.staticHandles.has(s.dataset.productHandle)) s.remove();
      });
      swiper?.swiper?.update();
    }

    initNavigation() {
      const swiperEl = this.querySelector('swiper-container');
      const prevBtn  = this.querySelector('[data-upsell-nav="prev"]');
      const nextBtn  = this.querySelector('[data-upsell-nav="next"]');
      if (!swiperEl || !prevBtn || !nextBtn) return;

      this.updateNav = () => {
        const s = swiperEl.swiper;
        if (!s) return;
        prevBtn.classList.toggle('swiper-button-disabled', s.isBeginning);
        nextBtn.classList.toggle('swiper-button-disabled', s.isEnd);
      };

      prevBtn.addEventListener('click', () => {
        swiperEl.swiper?.slidePrev();
        requestAnimationFrame(() => this.updateNav?.());
      });
      nextBtn.addEventListener('click', () => {
        swiperEl.swiper?.slideNext();
        requestAnimationFrame(() => this.updateNav?.());
      });

      const bindSwiperEvents = () => {
        const s = swiperEl.swiper;
        if (!s) return;
        s.on('slideChange progress transitionEnd reachBeginning reachEnd fromEdge touchEnd', this.updateNav);
        this.updateNav();
      };

      if (swiperEl.swiper) {
        bindSwiperEvents();
      } else {
        swiperEl.addEventListener('swiperinitialized', bindSwiperEvents, { once: true });
        setTimeout(bindSwiperEvents, 100);
      }
    }

    handleATC(btn) {
      const card    = btn.closest('product-card');
      const select  = card?.querySelector('[data-upsell-variant-select]');
      const errorEl = card?.querySelector('[data-upsell-error]');

      let variantId = btn.dataset.variantId;
      if (!variantId && select) variantId = select.value;

      if (!variantId) {
        errorEl?.classList.remove('hidden');
        return;
      }
      errorEl?.classList.add('hidden');

      btn.disabled = true;
      const originalHTML = btn.innerHTML;
      btn.innerHTML = '<svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>';

      window.dispatchEvent(new Event('cart:add:pending'));

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          ...(window.minicartSectionId && { sections: [window.minicartSectionId] }),
        }),
      })
        .then(r => r.json())
        .then(data => {
          btn.innerHTML = '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          window.dispatchEvent(new CustomEvent('productForm:add', { detail: data }));
          setTimeout(() => {
            btn.innerHTML = originalHTML;
            btn.disabled = false;
          }, 1500);
        })
        .catch(() => {
          btn.innerHTML = originalHTML;
          btn.disabled = false;
          if (errorEl) errorEl.textContent = 'Error adding item. Please try again.';
          errorEl?.classList.remove('hidden');
        });
    }

    async fetchAndAppendDynamic(limit) {
      this.dynamicFetchDone = false;
      try {
        const cartItems = [...document.querySelectorAll('[data-cart-product-id][data-product-tags]')].map(el => ({
          product_tags: el.dataset.productTags,
        }));

        const { query } = this.buildQuery(cartItems);

        let handles = [];
        if (query) {
          handles = await this.fetchPredictive(query, limit);
        } else if (this.dataset.fallbackHandle) {
          handles = await this.fetchCollectionHandles(this.dataset.fallbackHandle, limit);
        } else {
          return;
        }

        const toAdd = handles
          .filter(h => !this.renderedHandles.has(h) && !this.cartProductHandles.has(h))
          .slice(0, limit);

        if (toAdd.length === 0) return;

        const swiper = this.querySelector('swiper-container');
        if (!swiper) return;

        const slidesHtml = toAdd.map(handle => {
          this.renderedHandles.add(handle);
          return `<swiper-slide class="w-40" data-product-handle="${handle}"><intersection-loader data-url="/products/${handle}" data-section-id="upsell-product-card" data-load-immediately="true"></intersection-loader></swiper-slide>`;
        }).join('');
        swiper.insertAdjacentHTML('beforeend', slidesHtml);

        if (swiper.swiper) swiper.swiper.update();
        this.updateNav?.();
        this.updateSlideSignal();
      } catch (err) {
        console.warn('UpsellCarousel: error fetching dynamic products', err);
      } finally {
        this.dynamicFetchDone = true;
        this.updateSlideSignal();
      }
    }

    async fetchPredictive(query, limit) {
      const fetchLimit = limit + this.renderedHandles.size + this.cartProductHandles.size;
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${fetchLimit}&resources[fields]=title,product_type,tag`;
      const result = await fetch(url).then(r => r.json());
      return (result?.resources?.results?.products || [])
        .filter(p => p.available && !(parseInt(p.compare_at_price_max, 10) > parseInt(p.price_min, 10)))
        .filter(p => !this.isAppExclusive(p))
        .map(p => p.handle);
    }

    async fetchCollectionHandles(handle, limit) {
      const fetchLimit = limit + this.renderedHandles.size + this.cartProductHandles.size;
      const data = await fetch(`/collections/${handle}/products.json?limit=${fetchLimit}`).then(r => r.json());
      return (data.products || [])
        .filter(p => p.variants?.some(v => v.available) && !(p.compare_at_price_max > p.price_min))
        .filter(p => !this.isAppExclusive(p))
        .map(p => p.handle);
    }

    // tapcart_exclusive / tapcart_exclusive_ATC products can't be bought on web, so never suggest them
    isAppExclusive(p) {
      const tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',');
      return tags.some(t => ['tapcart_exclusive', 'tapcart_exclusive_atc'].includes(t.trim().toLowerCase()));
    }

    buildQuery(items) {
      const teamTags   = new Set();
      const genderTags = new Set();
      const types      = new Set();

      items.forEach(item => {
        let tags = item.product_tags;
        if (typeof tags === 'string') tags = tags.split(',').map(t => t.trim());
        if (!Array.isArray(tags)) return;

        tags.forEach(tag => {
          const lower = tag.toLowerCase().trim();
          if      (lower.startsWith('team:'))   teamTags.add(lower.slice(5));
          else if (lower.startsWith('gender:')) genderTags.add(lower.slice(7));
          else if (lower.startsWith('type:'))   types.add(lower.slice(5));
        });
      });

      const parts = [];
      teamTags.forEach(t   => parts.push(`tag:"team:${t}"`));
      genderTags.forEach(g => parts.push(`tag:"gender:${g}"`));

      if (types.size === 1) {
        const [type] = types;
        if (type === 'headwear') {
          parts.push(`tag:"type:apparel"`);
        } else if (type === 'apparel') {
          parts.push(`tag:"type:headwear"`);
        }
      }

      return { query: parts.join(' OR '), teamTags, genderTags, types };
    }

    updateSlideSignal() {
      const hasSlides = this.querySelectorAll('swiper-slide').length > 0;
      const dynamicLimit = parseInt(this.dataset.dynamicLimit, 10) || 0;
      const shouldHide = !hasSlides && (dynamicLimit === 0 || this.dynamicFetchDone);
      const header = this.querySelector('.upsell-header');
      if (header) header.classList.toggle('hidden', shouldHide);
      this.style.display = shouldHide ? 'none' : 'block';
      const sentinel = this.querySelector('.upsell-items_li');
      if (sentinel) sentinel.classList.toggle('hidden', !hasSlides);
    }
  }

  customElements.define('upsell-carousel', UpsellCarousel);
}
